import React, { useState, useEffect, useRef, useCallback } from "react";
import { Col, Row, Card } from "react-bootstrap";
import { Link } from "react-router-dom";
import { useDispatch } from "react-redux";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-icons/font/bootstrap-icons.css";
import Aos from "aos";
import "aos/dist/aos.css";
import Footer from "./Footer";
import Navbarsticky from "./NavbarSticky";
import { getcarrentalPackageList } from "../store/slices/carrentalpackage";
import Skeleton from "react-loading-skeleton";
import { OverlayTrigger, Popover } from "react-bootstrap";

const RATINGS = [4, 4.5, 5];

const getRandomRating = () =>
  RATINGS[Math.floor(Math.random() * RATINGS.length)];

const renderStars = (rating) => {
  const fullStars = Math.floor(rating);
  const halfStar = rating % 1 !== 0;

  return (
    <>
      {[...Array(fullStars)].map((_, i) => (
        <i key={`full-${i}`} className="bi bi-star-fill text-warning me-1"></i>
      ))}

      {halfStar && <i className="bi bi-star-half text-warning me-1"></i>}

      {[...Array(5 - Math.ceil(rating))].map((_, i) => (
        <i key={`empty-${i}`} className="bi bi-star text-warning me-1"></i>
      ))}
    </>
  );
};

export default function CarPackage() {
  const dispatch = useDispatch();
  const [isDragging, setIsDragging] = useState(false);

  const [sortBy, setSortBy] = useState("default");

  const [page, setPage] = useState(1);
  const [packages, setPackages] = useState([]);

  const [isLoading, setIsLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);

  const loaderRef = useRef(null);
  const isLoadingRef = useRef(false);
  const hasMoreRef = useRef(true);

  useEffect(() => {
    isLoadingRef.current = isLoading;
    hasMoreRef.current = hasMore;
  }, [isLoading, hasMore]);

  useEffect(() => {
    Aos.init({ duration: 900, once: true });
  }, []);

  const buildFilters = useCallback(() => {
    const filters = { page, limit: 6 };

    return filters;
  }, [page]);

  const loadPackages = useCallback(async () => {
    if (isLoadingRef.current || !hasMoreRef.current) return;

    setIsLoading(true);

    try {
      const res = await dispatch(
        getcarrentalPackageList(buildFilters())
      ).unwrap();
      const items = res?.carrentalpackages?.packageData || [];

      const enrichedItems = items.map((item) => ({
        ...item,
        rating: getRandomRating(),
      }));

      setPackages((prev) =>
        page === 1 ? enrichedItems : [...prev, ...enrichedItems]
      );

      setHasMore(items.length === 6);
    } catch (err) {
      console.error(err);
    }

    setIsLoading(false);
  }, [dispatch, buildFilters, page]);

  useEffect(() => {
    loadPackages();
  }, [loadPackages]);

  useEffect(() => {
    if (isDragging) return;

    setPage(1);
    setHasMore(true);
    setPackages([]);
  }, [isDragging]);

  // Infinite scroll
  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      if (
        entries[0].isIntersecting &&
        hasMoreRef.current &&
        !isLoadingRef.current
      ) {
        setPage((p) => p + 1);
      }
    });

    if (loaderRef.current) observer.observe(loaderRef.current);
    return () => observer.disconnect();
  }, []);

  // Sorting
  const sortedPackages = [...packages].sort((a, b) => {
    if (sortBy === "price_asc") return a.packagebaseRate - b.packagebaseRate;
    if (sortBy === "price_desc") return b.packagebaseRate - a.packagebaseRate;
    return 0;
  });

  return (
    <>
      <Navbarsticky />
      <section className="page-container hero-section text-center">
        <h1 className="fw-bold text-white hero-title">Andaman Island Escape</h1>
        <p className="text-white hero-subtitle">
          Unwind amid crystal-clear seas and serene white shores.
        </p>
      </section>
      <section className="container p-4 my-4">
        <div className="row g-4">
          <div className="col-12 col-md-12 col-lg-12">
            <div className="d-flex justify-content-end align-items-center mb-4 flex-wrap">
              <h6 className="fw-semibold me-2">
                Showing {sortedPackages.length} Results
              </h6>
            </div>

            <Row xs={2} sm={2} lg={3} className="g-2 g-lg-4">
              {isLoading && page === 1
                ? Array.from({ length: 6 }).map((_, i) => (
                    <Col key={i}>
                      <div className="destination-item bgc-lighter">
                        <Skeleton height={200} borderRadius="8px" />
                        <Skeleton count={3} className="mt-2" />
                      </div>
                    </Col>
                  ))
                : sortedPackages.map((item, index) => (
                    <Col key={index}>
                      <Link to={`/car-package-details/${item.slug}`}>
                        <Card className="tour-card shadow-sm">
                          <img
                            src={item.images[0]?.mediaLink}
                            className="tour-img"
                            alt=""
                          />
                          <Card.Body>
                            {/* Package Title */}
                            <p className="pkg-title">{item.packageName}</p>

                            {/* Places Popover */}
                            <OverlayTrigger
                              placement="bottom"
                              trigger={["hover", "focus"]}
                              overlay={
                                <Popover
                                  id="place-popover"
                                  className="place-popover"
                                >
                                  <Popover.Body>
                                    <div className="fw-bold mb-2">
                                      {item.stateName ||
                                        "Andaman & Nicobar Islands"}
                                    </div>

                                    <ul className="place-list">
                                      {item.places?.map((p, idx) => (
                                        <li key={idx} className="text-muted">
                                          {p.name}
                                        </li>
                                      ))}
                                    </ul>
                                  </Popover.Body>
                                </Popover>
                              }
                            >
                              <Card.Text className="location-text">
                                <i className="bi bi-geo-alt-fill text-primary me-1"></i>

                                {(() => {
                                  const names =
                                    item.places?.map((p) => p.name) || [];
                                  const firstTwo = names.slice(0, 2).join(", ");
                                  const remaining =
                                    names.length > 2
                                      ? ` + ${names.length - 2} more places`
                                      : "";
                                  return firstTwo + remaining;
                                })()}
                              </Card.Text>
                            </OverlayTrigger>

                            {/* Duration */}
                            <Card.Text className="text-muted">
                              <i className="bi bi-calendar-event text-primary me-1"></i>
                              {item.duration}
                            </Card.Text>

                            {/* Rating */}
                            <div className="card-footer-section d-flex justify-content-between align-items-center">
                              <div className="rating d-flex align-items-center">
                                {renderStars(item.rating)}
                                <span className="text-muted ms-1">
                                  ({item.rating})
                                </span>
                              </div>
                            </div>
                          </Card.Body>
                        </Card>
                      </Link>
                    </Col>
                  ))}

              <div ref={loaderRef} style={{ height: "20px" }} />
              {isLoading && page > 1 && (
                <div className="col-12 text-center">
                  <Skeleton width={200} height={20} />
                </div>
              )}
            </Row>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
}
